import { Component } from '@theme/component';
// Horizon 4.x replaced @theme/events' CartAddEvent with the standard
// CartLinesUpdateEvent, whose result arrives on a promise ({ cart, detail }).
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that adds checked add-on products to the cart right after
 * the main product is added by the product form. The bundle discount itself is
 * applied server-side by the NIXY Bundle Savings discount function.
 *
 * @typedef {{item_count: number, items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @extends {Component}
 */
class ProductAddonsComponent extends Component {
  #abortController = new AbortController();

  /**
   * The hidden `properties[Package]` input this component injected into the
   * main product form while the board-only checkbox is checked. Kept as a ref
   * so unchecking removes exactly the input this instance created (each
   * quick-add clone is its own component instance with its own ref).
   * @type {HTMLInputElement | null}
   */
  #boardOnlyInput = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });

    // Delegated on the component so it covers every card, including quick-add
    // clones of this component's content. 'change' bubbles from the selects.
    this.addEventListener('change', this.#onVariantSelectChange, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('change', this.#onBoardOnlyChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Keeps the card's checkbox pointing at the variant chosen in the card's
   * [data-addon-select] dropdown, and swaps the card thumbnail and preview
   * dialog image to the selected variant's image (option data attributes,
   * rendered in Liquid). Options without image data (variant has no assigned
   * image) leave the current images untouched. The add-to-cart flow
   * (#onCartUpdate) reads data-addon-variant-id from the dataset at click
   * time, so the attribute update is the only cart wiring the selector needs.
   * @param {Event} event - The change event.
   */
  #onVariantSelectChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-addon-select')) return;

    const item = select.closest('.product-addons__item');

    const checkbox = item?.querySelector('input[data-addon-variant-id]');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.dataset.addonVariantId = select.value;
    }

    const option = select.selectedOptions[0];
    const smallSrc = option?.dataset.variantImageSmall;
    if (!smallSrc) return;

    const thumb = item?.querySelector('.product-addons__image');
    if (thumb instanceof HTMLImageElement) {
      thumb.src = smallSrc;
    }

    const dialogImage = item?.querySelector('.product-addons__dialog-image');
    const largeSrc = option.dataset.variantImageLarge;
    if (dialogImage instanceof HTMLImageElement && largeSrc) {
      dialogImage.src = largeSrc;
      const height = option.dataset.variantImageHeight;
      if (height) {
        dialogImage.height = Number(height);
      }
    }
  };

  /**
   * Finds the main product form (buy-buttons block) rendered in the SAME
   * context as this component. Scoping mirrors product-form.js
   * connectedCallback (`this.closest('.shopify-section, dialog, product-card')`):
   * on the main product page the scope is the product-details section; inside
   * the quick-add modal — which clones the whole product content — the scope
   * is the modal's <dialog>, so the lookup resolves to the CLONED form, never
   * the background page's. The data-product-id filter guards against sections
   * that render more than one product form.
   * @returns {HTMLFormElement | null}
   */
  #findProductForm() {
    const scope = this.closest('.shopify-section, dialog, product-card');
    const form = scope?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] form[data-type="add-to-cart-form"]`
    );
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * Handles the board-only ([data-board-only]) checkbox.
   *
   * Checked: injects a hidden `properties[Package]="Board only"` line item
   * property into the scoped main product form (so the board line carries it
   * to the discount function), and disables every [data-package-only] addon
   * card in this component — unchecked + disabled checkbox, disabled variant
   * select, dimmed via CSS class (which also sets pointer-events: none, making
   * the whole card, preview triggers included, non-interactive).
   *
   * Unchecked: removes the hidden input and re-enables the cards WITHOUT
   * re-checking them — the customer re-opts-in explicitly. The exception is
   * [data-included] entries (free items with no visible checkbox), which are
   * restored to their checked default: there is no control on screen for the
   * customer to re-opt-in with, and they never opted out in the first place.
   *
   * If no product form is found (edge rendering contexts), the property
   * injection is silently skipped: the checkbox still works visually and the
   * package-only disabling still applies, but the cart line simply won't carry
   * the property — a safe no-op rather than a thrown error.
   * @param {Event} event - The change event.
   */
  #onBoardOnlyChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.hasAttribute('data-board-only')) return;

    const packageOnlyItems = this.querySelectorAll('[data-package-only]');

    if (checkbox.checked) {
      const form = this.#findProductForm();
      // A full-container morph (e.g. combined-listing variant change) can remove
      // the injected input without firing change, leaving a stale detached ref —
      // isConnected re-arms injection in that case instead of silently skipping it.
      if (this.#boardOnlyInput && !this.#boardOnlyInput.isConnected) {
        this.#boardOnlyInput = null;
      }
      if (form && !this.#boardOnlyInput) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Package]';
        input.value = 'Board only';
        form.appendChild(input);
        this.#boardOnlyInput = input;
      }

      for (const item of packageOnlyItems) {
        item.classList.add('product-addons__item--disabled');
        const addonCheckbox = item.querySelector('input[data-addon-variant-id]');
        if (addonCheckbox instanceof HTMLInputElement) {
          addonCheckbox.checked = false;
          addonCheckbox.disabled = true;
        }
        const select = item.querySelector('[data-addon-select]');
        if (select instanceof HTMLSelectElement) {
          select.disabled = true;
        }
      }
    } else {
      this.#boardOnlyInput?.remove();
      this.#boardOnlyInput = null;

      for (const item of packageOnlyItems) {
        item.classList.remove('product-addons__item--disabled');
        const addonCheckbox = item.querySelector('input[data-addon-variant-id]');
        if (addonCheckbox instanceof HTMLInputElement) {
          addonCheckbox.disabled = false;
          // A customer's own opt-in is never restored on their behalf. An INCLUDED entry
          // is different: it is a free item that ships with the product, was on by
          // default, and was force-cleared above — not something the customer declined.
          // It also has no visible checkbox, so leaving it off would silently drop a free
          // upgrade with no control on screen to notice or undo it. Restore the DEFAULT.
          if (addonCheckbox.hasAttribute('data-included')) {
            addonCheckbox.checked = true;
          }
        }
        const select = item.querySelector('[data-addon-select]');
        if (select instanceof HTMLSelectElement) {
          select.disabled = false;
        }
      }
    }
  };

  /**
   * Waits for the product form's add to settle, then adds the checked add-ons.
   * @param {CartLinesUpdateEvent} event - The cart lines update event.
   */
  #onCartUpdate = (event) => {
    // Events dispatched by any product-addons component (including this one)
    // must never re-trigger an add-on add, regardless of their source value.
    if (event.target instanceof ProductAddonsComponent) return;
    if (event.action !== 'add' || !event.promise) return;

    event.promise.then(({ detail }) => this.#addCheckedAddons(detail)).catch(() => {});
  };

  /**
   * Adds the checked add-ons after the main product was added to the cart.
   * @param {{source?: string, didError?: boolean, productId?: string} | undefined} detail - The settled add's detail.
   */
  #addCheckedAddons = async (detail) => {
    // Only react to a successful add of this component's product by the product form.
    if (detail?.source !== 'product-form-component') return;
    if (detail.didError) return;
    if (String(detail.productId) !== String(this.dataset.productId)) return;

    const checkedInputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('input[type="checkbox"][data-addon-variant-id]:checked'))
    );
    if (checkedInputs.length === 0) return;

    const items = checkedInputs
      .map((input) => ({
        id: Number(input.dataset.addonVariantId),
        quantity: 1,
      }))
      .filter((item) => Number.isFinite(item.id));

    // Uncheck immediately so re-adding the main product doesn't re-add the add-ons.
    // Included (free) items stay checked: every board added again earns one.
    for (const input of checkedInputs) {
      if (!input.hasAttribute('data-included')) input.checked = false;
    }

    // Nothing valid to add (malformed variant ids). Leave the boxes unchecked.
    if (items.length === 0) {
      console.error('product-addons: all data-addon-variant-id values are malformed, check the nixy.addons metafield');
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    /** @type {{status?: number, message?: string, sections?: Record<string, string>} | null} */
    let result;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
        }),
      });

      result = await response.json().catch(() => null);

      if (!response.ok || !result || result.status) {
        console.error('Failed to add add-ons to cart:', result?.message ?? `HTTP ${response.status}`);
        // Restore the customer's selection so clicking Add to Cart again retries.
        for (const input of checkedInputs) {
          input.checked = true;
        }
        return;
      }
    } catch (error) {
      console.error('Failed to add add-ons to cart:', error);
      // Restore the customer's selection so clicking Add to Cart again retries.
      for (const input of checkedInputs) {
        input.checked = true;
      }
      return;
    }

    // The add-ons are now in the cart. Announce it so the cart drawer, cart page
    // and cart icon refresh. cart-icon.js sets the badge to cart.totalQuantity,
    // so the event needs the absolute cart; if /cart.js fails, skip the event
    // (the lines are in the cart and show on the next render) rather than
    // zeroing the badge.
    /** @type {Cart | undefined} */
    let cart;
    try {
      cart = await fetch('/cart.js').then((response) => response.json());
    } catch {
      cart = undefined;
    }
    if (!cart) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'product-addons-component',
        itemCount: cart.item_count,
        productId: this.dataset.productId,
        sections: result.sections,
        didError: false,
      },
    });
  };
}

if (!customElements.get('product-addons-component')) {
  customElements.define('product-addons-component', ProductAddonsComponent);
}
